import { ReadonlyVec3 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    AGREEMENT_COLOR,
    rgb8ToNormalized,
} from '~/engine/core/render/renderers/util/comparisonColors';
import { ComparisonOverlayRegion } from '~/engine/data/common/customs/ComparisonObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    BooleanOperation,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import {
    comparablePair,
    compareModeActiveState,
    comparisonRoiAState,
    comparisonRoiBState,
} from '~/state/atoms/structureSet/roiAtoms';
import { attachComparisonOverlays } from './comparisonOverlay';
import { useComparisonEditToken } from './useComparisonEditToken';
import { dicomColorToVec3 } from '~/utils/color';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Translucent green fill for the agreement region. Kept solid enough that the
 * varying grayscale anatomy underneath does not bleed through and read as
 * blotchy "spots" of missing fill, while still letting the image show.
 */
const AGREEMENT_FILL_ALPHA = 0.5;
/**
 * Edge thickness on the filled agreement region. Matches
 * {@link OUTLINE_THICKNESS}: where the agreement narrows to a sub-voxel sliver
 * (e.g. near a contour crossing), the translucent fill alone renders as faint
 * broken speckle, so a solid edge of this width keeps the thin agreement
 * boundary visible instead of disappearing.
 */
const FILL_EDGE_THICKNESS = 2.5;
/** Thicker edge for the fill-less ROI contour outlines so they read. */
const OUTLINE_THICKNESS = 2.5;

/** Each ROI's own colour, GPU-normalised; white fallback if it can't be found. */
function roiColorNormalized(
    structureSet: StructureSetReadModel | undefined,
    roiId: RoiId
): ReadonlyVec3 {
    const roi = structureSet?.roiArray.find((r) => r.roiId === roiId);
    if (!roi) {
        return [1, 1, 1];
    }
    return dicomColorToVec3(roi.color);
}

/**
 * Build the overlay regions from the two ROI SDF textures. Each call returns
 * fresh textures the caller (the {@link ComparisonObject}) then owns.
 *
 * Agreement (A∩B) is a translucent green fill computed with a single validated
 * intersection. The two ROIs are then drawn as their OWN contours — each a
 * clone of the live ROI texture, outlined in the ROI's own colour
 * (see {@link comparisonColors}).
 *
 * The disagreement is read directly from the geometry: the area inside an ROI's
 * outline but outside the green fill is where that ROI extends beyond the other.
 * Earlier this drew the boolean differences (A∖B, B∖A) as outlines, but those
 * are thin slivers whose two boundaries run nearly parallel where the contours
 * are close, so the SDF-discretised difference rendered as doubled "ghost"
 * lines. Outlining the real contours avoids that artifact entirely while
 * keeping the only boolean op (the agreement fill, which has no sliver to
 * double) intact.
 */
function buildComparisonRegions(
    texA: ContourTexture,
    texB: ContourTexture,
    roiAColor: ReadonlyVec3,
    roiBColor: ReadonlyVec3
): ComparisonOverlayRegion[] {
    const intersection = SDFOperations.applyBooleanUncropped(
        texA,
        texB,
        BooleanOperation.AND
    );

    // Clone the live ROI textures: the ComparisonObject owns and deletes the
    // region textures, so it must not hold the environment's own ROI textures.
    return [
        {
            texture: intersection,
            color: rgb8ToNormalized(AGREEMENT_COLOR),
            fillAlpha: AGREEMENT_FILL_ALPHA,
            thickness: FILL_EDGE_THICKNESS,
        },
        {
            texture: texA.clone(),
            color: roiAColor,
            fillAlpha: 0,
            thickness: OUTLINE_THICKNESS,
        },
        {
            texture: texB.clone(),
            color: roiBColor,
            fillAlpha: 0,
            thickness: OUTLINE_THICKNESS,
        },
    ];
}

/**
 * Drives the in-viewport contour comparison overlay. When compare mode is
 * active and two distinct ROIs are selected, it computes the colour-coded
 * difference map and draws it on the edit target's Environment only, on every
 * grid tile that shows that Environment (a second row of the same series
 * shares it). A view that mounts while the comparison is on receives the
 * overlay; a view that unmounts has it removed, which frees the textures the
 * view's own teardown leaves behind. While the active set sits on no view
 * nothing is drawn, and the overlay appears once a view shows it.
 * Mount once in a component that outlives the grid's tiles.
 *
 * The ROI colours come from the edit target's structure set, the set that
 * owns the textures, so the overlay and the set cannot disagree. Metric
 * numbers are handled separately by the comparison side panel; this hook is
 * purely the engine bridge (Recoil to custom render object).
 */
export function useContourComparison(): void {
    const compareModeActive = useRecoilValue(compareModeActiveState);
    const roiA = useRecoilValue(comparisonRoiAState);
    const roiB = useRecoilValue(comparisonRoiBState);
    const { environment, structureSet } = useActiveEditTarget();
    const views = useEnvironmentViews(environment);

    // Read the structure set via a ref for ROI colours: the read model is
    // re-created on every visibility change (the isolation hook flips
    // visibility), so depending on it here would needlessly rebuild the SDF
    // textures. Colours are captured when the effect runs for a real change
    // (compare toggle / pair / Environment / views).
    const structureSetRef = useRef(structureSet);
    structureSetRef.current = structureSet;

    // Bumped whenever a compared ROI's contour is edited, so the overlay
    // textures are rebuilt off the new SDF. Without this the overlay keeps
    // drawing the pre-edit shape, a stale "ghost" contour, over the live one.
    const editToken = useComparisonEditToken(roiA, roiB, compareModeActive);

    useEffect(() => {
        const pair = comparablePair(roiA, roiB);
        if (!compareModeActive || !environment || !pair || views.length === 0) {
            return;
        }
        const [referenceRoi, testRoi] = pair;

        // Point ROIs and not-yet-generated SDFs have no texture; skip rather
        // than render a misleading partial overlay.
        const texA = environment.getRoi(referenceRoi)?.texture;
        const texB = environment.getRoi(testRoi)?.texture;
        if (!texA || !texB) {
            return;
        }

        const ss = structureSetRef.current;
        const regions = buildComparisonRegions(
            texA,
            texB,
            roiColorNormalized(ss, referenceRoi),
            roiColorNormalized(ss, testRoi)
        );
        // Draw one overlay per view (each owning its own region-texture clones);
        // the returned teardown removes them and repaints.
        return attachComparisonOverlays(views, regions, environment);
    }, [compareModeActive, roiA, roiB, environment, views, editToken]);
}
